import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { roleActions } from "../slices/role";
import { getErrorMessage } from "../../utils/errorUtils";

import { RoleRequestDto } from "../../shared/dtos/role/RoleRequestDto";
import { RoleResponseDto } from "../../shared/dtos/role/RoleResponseDto";
import { UpdateRolePayload } from "../../shared/interfaces/role/UpdateRolePayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getRoles =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(roleActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<RoleResponseDto[]>
      >("/roles", {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(roleActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(roleActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getRoleById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(roleActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<RoleResponseDto>>(
        `/roles/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(roleActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(roleActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createRole =
  (values: RoleRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(roleActions.setLoadingCreate());
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim(),
      };
      const { data } = await axiosInstance.post<
        ApiResponseDto<RoleResponseDto>
      >("/roles", payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Role created successfully!");
        dispatch(roleActions.createSuccess(data.data));
        dispatch(
          roleActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(roleActions.setErrorCreate(message));
    }
  };

export const updateRole =
  (payload: UpdateRolePayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(roleActions.setLoadingUpdate());
    try {
      const updatePayload = {
        name: payload.values.name.trim(),
        description: payload.values.description?.trim(),
        permissions: payload.values.permissions || [],
      };
      const { data } = await axiosInstance.put<ApiResponseDto<RoleResponseDto>>(
        `/roles/${payload.id}`,
        updatePayload,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Role updated successfully!");
        dispatch(roleActions.updateSuccess(data.data));
        dispatch(
          roleActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(roleActions.setErrorUpdate(message));
    }
  };

export const deleteRole =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(roleActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/roles/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Role deleted successfully!");
        dispatch(roleActions.deleteSuccess(id));
        dispatch(
          roleActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(roleActions.setErrorDelete(message));
    }
  };

export const resetRoleServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(roleActions.setServerStatus(null));
